import { Box, Skeleton } from '@mui/material';
import type { Property } from 'csstype';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { FC, PropsWithChildren } from 'react';
import {
  FullscreenControl,
  Map as Mapbox,
  NavigationControl,
} from 'react-map-gl/mapbox';
import type { Bounds } from '../../utils/geo.js';

export const MAP_PADDING = 50;

interface Props {
  bearing: number;
  bounds?: Bounds;
  height?:
    | Property.Height<string | number>
    | NonNullable<Property.Height<string | number> | undefined>[]
    | undefined;
  mapStyle: string;
  maxHeight?:
    | Property.Height<string | number>
    | NonNullable<Property.Height<string | number> | undefined>[]
    | undefined;
  maxWidth?:
    | Property.Width<string | number>
    | NonNullable<Property.Width<string | number> | undefined>[]
    | undefined;
  minHeight?:
    | Property.Height<string | number>
    | NonNullable<Property.Height<string | number> | undefined>[]
    | undefined;
  minWidth?:
    | Property.Width<string | number>
    | NonNullable<Property.Width<string | number> | undefined>[]
    | undefined;
  width?:
    | Property.Width<string | number>
    | NonNullable<Property.Width<string | number> | undefined>[]
    | undefined;
}

export const MapView: FC<PropsWithChildren<Props>> = ({ bounds, ...props }) =>
  bounds ? (
    <MapWithBounds bounds={bounds} {...props} />
  ) : (
    <Box
      alignItems="center"
      display="flex"
      height={props.height ?? '100%'}
      justifyContent="center"
      width={props.height ?? '100%'}
    >
      <Skeleton sx={{ height: '100%', width: '100%' }} />
    </Box>
  );

export const MapWithBounds: FC<
  PropsWithChildren<Props & { bounds: Bounds }>
> = ({
  bearing,
  bounds,
  children,
  height = '100%',
  mapStyle,
  maxHeight,
  maxWidth,
  minHeight,
  minWidth,
  width = '100%',
}) => (
  <Box
    height={height}
    maxHeight={maxHeight}
    maxWidth={maxWidth}
    minHeight={minHeight}
    minWidth={minWidth}
    position="relative"
    sx={{
      '& .mapboxgl-ctrl-attrib': {
        fontSize: '0.5rem',
        opacity: 0.8,
      },
      '& .mapboxgl-ctrl-logo': {
        opacity: 0.6,
      },
    }}
    width={width}
  >
    <Mapbox
      cooperativeGestures={false}
      initialViewState={{
        bearing,
        bounds,
        fitBoundsOptions: {
          padding: {
            bottom: MAP_PADDING,
            left: MAP_PADDING,
            right: MAP_PADDING,
            top: MAP_PADDING,
          },
        },
      }}
      interactiveLayerIds={['unclustered-layer-incidents']}
      mapboxAccessToken={import.meta.env.VITE_APP_MAPBOX_TOKEN as string}
      mapStyle={mapStyle}
      minZoom={11.5}
    >
      <FullscreenControl />
      <NavigationControl showCompass={false} />
      {children}
    </Mapbox>
  </Box>
);
